import { Constants } from "./constants";
import { ShowTypes } from "./show-type-definitions";

/**
 * AKC Conformation class helpers shared by the show editor, ring sync,
 * judge schedule / run order UI, catalog and judging-schedule exports.
 *
 * Single source of truth for:
 *  - which `show_element` a class level is stored under on scores
 *  - which classes are divided by sex (AKC Rules Applying to Dog Shows, Ch. 3 §3, §11, §12)
 *  - the order of judging within a breed
 *      Sweepstakes → Regular (all Dogs, then all Bitches) → Veterans / non-regular
 *      single-dog classes → Stud Dog / Brood Bitch / Brace → Best of Breed → Junior Showmanship
 */

const L = Constants.dog_class_element_level;
const E = Constants.dog_class_element;

export type AkcConformationSection =
  | "puppy_sweepstakes"
  | "veteran_sweepstakes"
  | "dual_sweepstakes"
  | "regular"
  | "veteran"
  | "non_regular"
  | "best_of_breed"
  | "junior_showmanship"
  | "other";

export const AKC_CONFORMATION_PUPPY_SWEEPSTAKES_LEVELS: string[] = [
  L.akc_puppy_sweepstakes_6_9,
  L.akc_puppy_sweepstakes_9_12,
  L.akc_puppy_sweepstakes_12_15,
  L.akc_puppy_sweepstakes_15_18,
  L.akc_puppy_sweepstakes_12_18,
  L.akc_puppy_sweepstakes_bred_by_exhibitor_6_18,
  L.akc_puppy_sweepstakes_18_24,
  L.akc_puppy_sweepstakes, // deprecated fallback
];

export const AKC_CONFORMATION_VETERAN_SWEEPSTAKES_LEVELS: string[] = [
  L.akc_veteran_sweepstakes_7_9,
  L.akc_veteran_sweepstakes_7_10,
  L.akc_veteran_sweepstakes_8_10,
  L.akc_veteran_sweepstakes_8_11,
  L.akc_veteran_sweepstakes_9_plus,
  L.akc_veteran_sweepstakes_10_12,
  L.akc_veteran_sweepstakes_10_13,
  L.akc_veteran_sweepstakes_10_plus,
  L.akc_veteran_sweepstakes_11_plus,
  L.akc_veteran_sweepstakes_12_plus,
  L.akc_veteran_sweepstakes_13_plus,
];

export const AKC_CONFORMATION_DUAL_SWEEPSTAKES_LEVELS: string[] = [
  L.akc_dual_title_sweepstakes_6m_7y,
  L.akc_dual_title_sweepstakes_7y_plus,
  L.akc_dual_title_sweepstakes_7y_11y,
  L.akc_dual_title_sweepstakes_10y_plus,
  L.akc_dual_title_sweepstakes_11y_plus,
];

/** Puppy → Veteran → Dual-Titled sweepstakes; each is its own competition. */
export const AKC_CONFORMATION_SWEEPSTAKES_LEVELS: string[] = [
  ...AKC_CONFORMATION_PUPPY_SWEEPSTAKES_LEVELS,
  ...AKC_CONFORMATION_VETERAN_SWEEPSTAKES_LEVELS,
  ...AKC_CONFORMATION_DUAL_SWEEPSTAKES_LEVELS,
];

export const AKC_CONFORMATION_REGULAR_LEVELS: string[] = [
  L.akc_4_6_month_puppy,
  L.akc_4_6_month_puppy_exhibitor_only,
  L.akc_6_9_month_puppy,
  L.akc_9_12_month_puppy,
  L.akc_12_15_month_puppy,
  L.akc_15_18_month_puppy,
  L.akc_12_18_month_puppy,
  L.akc_18_24_month_puppy,
  L.akc_novice,
  L.akc_amateur_owner_handler,
  L.akc_under_12_month_bred_by_exhibitor,
  L.akc_adult_bred_by_exhibitor,
  L.akc_american_bred,
  L.akc_open,
  L.akc_miscellaneous,
  L.akc_exhibition_only,
];

export const AKC_CONFORMATION_VETERAN_LEVELS: string[] = [
  L.akc_veteran_6_8_years,
  L.akc_veteran_6_plus_years,
  L.akc_veteran_7_plus_years,
  L.akc_veteran_7_9_years,
  L.akc_veteran_7_10_years,
  L.akc_veteran_8_10_years,
  L.akc_veteran_8_11_years,
  L.akc_veteran_8_plus_years,
  L.akc_veteran_9_plus_years,
  L.akc_veteran_10_12_years,
  L.akc_veteran_10_plus_years,
  L.akc_veteran_11_plus_years,
  L.akc_veteran_12_plus_years,
  L.akc_veteran_13_plus_years,
];

/**
 * Non-regular classes offered by the club (Ch. 3 §12). Judged after the regular
 * classes and before Best of Breed. Sex-divided ones first, then the combined
 * "multiple dog" classes (Stud Dog / Brood Bitch / Brace) and exhibitions.
 */
export const AKC_CONFORMATION_NON_REGULAR_LEVELS: string[] = [
  L.akc_hunting_class,
  L.akc_field_trial_class,
  L.akc_coursing,
  L.akc_sled_dog,
  L.akc_futurity,
  L.akc_stud_dog,
  L.akc_brood_bitch,
  L.akc_brace,
  L.akc_teams,
  L.akc_parade_of_titleholders,
  L.akc_best_head,
  L.akc_best_movement,
  L.akc_best_field,
];

export const AKC_CONFORMATION_JUNIOR_SHOWMANSHIP_LEVELS: string[] = [
  L.akc_junior_showmanship,
  L.akc_junior_showmanship_novice,
  L.akc_junior_showmanship_open,
  L.akc_junior_showmanship_master,
  L.akc_novice_junior,
  L.akc_novice_intermediate,
  L.akc_novice_senior,
  L.akc_open_junior,
  L.akc_open_intermediate,
  L.akc_open_senior,
  L.akc_master_class,
];

/**
 * Flat order of judging. Best of Breed is last among breed classes because
 * undefeated winners of the non-regular single-dog classes (Veterans, Hunting,
 * Field Trial, ...) must be eligible to enter it.
 */
export const AKC_CONFORMATION_CLASS_ORDER: string[] = [
  ...AKC_CONFORMATION_SWEEPSTAKES_LEVELS,
  ...AKC_CONFORMATION_REGULAR_LEVELS,
  ...AKC_CONFORMATION_VETERAN_LEVELS,
  ...AKC_CONFORMATION_NON_REGULAR_LEVELS,
  L.akc_best_of_breed,
  ...AKC_CONFORMATION_JUNIOR_SHOWMANSHIP_LEVELS,
];

/**
 * Classes that are NOT divided by sex.
 * Rulebook: Brace / Team are explicitly "not divided by sex"; Best of Breed is the
 * intersex competition; Stud Dog / Brood Bitch / Parade are single-sex or exhibition
 * classes; Junior Showmanship is divided by handler age/experience.
 */
export const AKC_CONFORMATION_NO_SEX_SPLIT_LEVELS: string[] = [
  L.akc_best_of_breed,
  L.akc_brace,
  L.akc_brood_bitch,
  L.akc_stud_dog,
  L.akc_coursing,
  L.akc_sled_dog,
  L.akc_teams,
  L.akc_parade_of_titleholders,
  L.akc_futurity,
  L.akc_best_head,
  L.akc_best_movement,
  L.akc_best_field,
  ...AKC_CONFORMATION_JUNIOR_SHOWMANSHIP_LEVELS,
];

/** Regular classes that may additionally be divided by breed variety / colour. */
export const AKC_CONFORMATION_VARIATION_SPLIT_LEVELS: string[] = [
  L.akc_open,
  L.akc_american_bred,
  L.akc_under_12_month_bred_by_exhibitor,
  L.akc_adult_bred_by_exhibitor,
];

/**
 * Levels that the show editor groups under "Non-Licensed Classes" but which
 * registrations store under show_element "Conformation".
 */
const STORED_AS_CONFORMATION: string[] = [
  L.akc_stud_dog,
  L.akc_brood_bitch,
  L.akc_brace,
  L.akc_coursing,
];

const lower = (value?: string | null): string => (value || "").trim().toLowerCase();

const includesLevel = (list: string[], level?: string | null): boolean => {
  const needle = lower(level);
  return needle !== "" && list.some((l) => lower(l) === needle);
};

const nonLicensedLevels = (): string[] =>
  (ShowTypes?.[Constants.sanctioning_club.AKC]?.[Constants.show_type.conformation]?.[E.conformation_non_licensed] as string[]) || [];

export const isAKCConformationJuniorShowmanshipLevel = (level?: string | null): boolean =>
  includesLevel(AKC_CONFORMATION_JUNIOR_SHOWMANSHIP_LEVELS, level);

/**
 * Returns the `show_element` a registration/score for this class is stored under.
 * This is what ring tiles, catalog and exports must use when matching scores.
 */
export const normalizeAKCConformationShowElement = (level?: string | null, showElement?: string | null): string => {
  if (showElement === E.junior_showmanship || isAKCConformationJuniorShowmanshipLevel(level)) {
    return E.junior_showmanship;
  }
  if (includesLevel(STORED_AS_CONFORMATION, level)) {
    return E.conformation;
  }
  if (includesLevel(nonLicensedLevels(), level) || includesLevel(AKC_CONFORMATION_SWEEPSTAKES_LEVELS, level)) {
    return E.conformation_non_licensed;
  }
  if (showElement && showElement.trim() !== "") {
    return showElement;
  }
  return E.conformation;
};

/** Key used to match ring tiles to scores regardless of how the element was labelled. */
export const getAKCConformationClassKey = (cls: { show_element?: string | null; level?: string | null }): string =>
  `${normalizeAKCConformationShowElement(cls.level, cls.show_element)}|${(cls.level || "").trim()}`;

export const akcConformationClassMatches = (
  a: { show_element?: string | null; level?: string | null },
  b: { show_element?: string | null; level?: string | null }
): boolean => getAKCConformationClassKey(a).toLowerCase() === getAKCConformationClassKey(b).toLowerCase();

export const getAKCConformationClassSection = (level?: string | null, showElement?: string | null): AkcConformationSection => {
  if (showElement === E.junior_showmanship || isAKCConformationJuniorShowmanshipLevel(level)) return "junior_showmanship";
  if (lower(level) === lower(L.akc_best_of_breed)) return "best_of_breed";
  if (includesLevel(AKC_CONFORMATION_PUPPY_SWEEPSTAKES_LEVELS, level)) return "puppy_sweepstakes";
  if (includesLevel(AKC_CONFORMATION_VETERAN_SWEEPSTAKES_LEVELS, level)) return "veteran_sweepstakes";
  if (includesLevel(AKC_CONFORMATION_DUAL_SWEEPSTAKES_LEVELS, level)) return "dual_sweepstakes";
  if (includesLevel(AKC_CONFORMATION_REGULAR_LEVELS, level)) return "regular";
  if (includesLevel(AKC_CONFORMATION_VETERAN_LEVELS, level)) return "veteran";
  if (includesLevel(AKC_CONFORMATION_NON_REGULAR_LEVELS, level)) return "non_regular";
  return "other";
};

const SECTION_ORDER: AkcConformationSection[] = [
  "puppy_sweepstakes",
  "veteran_sweepstakes",
  "dual_sweepstakes",
  "regular",
  "veteran",
  "non_regular",
  "other",
  "best_of_breed",
  "junior_showmanship",
];

/**
 * Sections where every Dogs class runs before the first Bitches class
 * (a Winners / Best in Sweeps decision separates the sexes). Everything else
 * (non-regular single classes) runs Dogs then Bitches within each class.
 */
const SEX_BLOCK_SECTIONS = new Set<AkcConformationSection>([
  "puppy_sweepstakes",
  "veteran_sweepstakes",
  "dual_sweepstakes",
  "regular",
  "veteran",
]);

export const getAKCConformationSectionIndex = (section: AkcConformationSection): number => {
  const idx = SECTION_ORDER.indexOf(section);
  return idx === -1 ? SECTION_ORDER.length : idx;
};

export const getAKCConformationClassOrderIndex = (level?: string | null): number => {
  const needle = lower(level);
  const idx = AKC_CONFORMATION_CLASS_ORDER.findIndex((l) => lower(l) === needle);
  return idx === -1 ? AKC_CONFORMATION_CLASS_ORDER.length : idx;
};

export const shouldSplitAKCConformationClassBySex = (level?: string | null, showElement?: string | null): boolean => {
  if (showElement === E.junior_showmanship) return false;
  return !includesLevel(AKC_CONFORMATION_NO_SEX_SPLIT_LEVELS, level);
};

export const shouldSplitAKCConformationClassByVariation = (level?: string | null): boolean =>
  includesLevel(AKC_CONFORMATION_VARIATION_SPLIT_LEVELS, level);

export interface IAkcConformationOrderable {
  show_element?: string | null;
  level?: string | null;
  sex_split?: "Dogs" | "Bitches" | string | null;
  variation?: string | null;
}

/**
 * Order of judging comparator for ring tiles / schedule rows.
 * Within a section all Dogs classes run first, then all Bitches classes
 * (Winners Dog is decided before the first bitch class is called).
 */
export const compareAKCConformationClasses = (a: IAkcConformationOrderable, b: IAkcConformationOrderable): number => {
  const sectionOfA = getAKCConformationClassSection(a.level, a.show_element);
  const sectionA = getAKCConformationSectionIndex(sectionOfA);
  const sectionB = getAKCConformationSectionIndex(getAKCConformationClassSection(b.level, b.show_element));
  if (sectionA !== sectionB) return sectionA - sectionB;

  const sexRank = (s?: string | null): number => (s === "Dogs" ? 0 : s === "Bitches" ? 1 : 2);
  const sexDiff = sexRank(a.sex_split) - sexRank(b.sex_split);
  const levelDiff = getAKCConformationClassOrderIndex(a.level) - getAKCConformationClassOrderIndex(b.level);

  if (SEX_BLOCK_SECTIONS.has(sectionOfA)) {
    // All Dogs classes, then all Bitches classes (unsplit placeholders last).
    if (sexDiff !== 0) return sexDiff;
    if (levelDiff !== 0) return levelDiff;
  } else {
    // Class by class, Dogs then Bitches within the class.
    if (levelDiff !== 0) return levelDiff;
    if (sexDiff !== 0) return sexDiff;
  }

  return lower(a.variation).localeCompare(lower(b.variation));
};

export const sortAKCConformationClasses = <T extends IAkcConformationOrderable>(classes: T[]): T[] =>
  [...classes].sort(compareAKCConformationClasses);
